// w47 Envelope — catalog preview card.
//
// The full template opens on a candlelit wooden desk: a sealed ivory
// envelope stamped with an oxblood wax seal, which the guest taps to watch
// the flap open and a letter unfold. The catalog card compresses that
// splash into one still frame: warm ivory paper, a centered pale envelope
// with its triangular flap folded shut, a small oxblood wax-seal dot
// sitting at the point where the flap meets the body, and the couple's
// names + date set below in the card's ink color — with a soft warm-brown
// vignette closing in at the corners, echoing the candlelit desk.
import { type LayoutCtx } from "./_shared";

const PAPER = "#f4e8d2";
const ENVELOPE = "#faf3e2";
const FLAP = "#efe1c3";
const INK = "#3a2233";
const WAX = "#7a2230";
const GOLD = "#b78d4e";

export function EnvelopePreviewLayout({ names, date, font }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: PAPER,
        overflow: "hidden",
      }}
    >
      {/* Candle glow — a soft warm highlight above the envelope. */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "8%",
          width: "70%",
          height: "40%",
          transform: "translateX(-50%)",
          background: `radial-gradient(ellipse at 50% 0%, ${GOLD}33, transparent 70%)`,
        }}
      />

      {/* Envelope. */}
      <div
        style={{
          position: "absolute",
          left: "18%",
          right: "18%",
          top: "22%",
          height: "38%",
          borderRadius: 4,
          background: ENVELOPE,
          boxShadow: `0 6px 16px ${INK}33`,
          overflow: "hidden",
        }}
      >
        {/* Flap, folded shut. */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            height: "62%",
            background: FLAP,
            clipPath: "polygon(0 0, 100% 0, 50% 100%)",
            WebkitClipPath: "polygon(0 0, 100% 0, 50% 100%)",
          }}
        />

        {/* Wax seal, sitting where the flap point meets the body. */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "58%",
            width: "22%",
            aspectRatio: "1",
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: `radial-gradient(circle at 35% 30%, ${WAX}dd, ${WAX} 60%, #5a1622 100%)`,
            boxShadow: `0 2px 5px ${INK}44`,
          }}
        />
      </div>

      {/* Names + date beneath the envelope. */}
      <div
        style={{
          position: "absolute",
          left: "10%",
          right: "10%",
          top: "66%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: font,
            fontSize: 13,
            lineHeight: 1.25,
            color: INK,
            marginBottom: "6%",
          }}
        >
          {names}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "6%" }}>
          <div style={{ width: 22, height: 1, background: `${GOLD}88` }} />
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: WAX }} />
          <div style={{ width: 22, height: 1, background: `${GOLD}88` }} />
        </div>

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.24em",
            color: INK,
            opacity: 0.8,
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>

      {/* Soft warm-brown vignette. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse at 50% 45%, transparent 55%, #2a160f55 100%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
